import { Button, Input, Radio, Select, Space, Tooltip } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";

import { PERIOD_CALC_TYPES, nextColumnId } from "./reportingUtils";

const CHANGE_SHOW_OPTIONS = [
  { value: "pct", label: "%" },
  { value: "abs", label: "Amount" },
];

const rowStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 6,
  padding: "6px 8px",
  border: "1px solid #f0f0f0",
  borderRadius: 4,
  marginBottom: 6,
};

/**
 * Edits settings.periodCalcs of a pivot table: changes and CAGRs between two
 * periods. shownCats are the periods the table shows, used for the defaults
 * and for "Change from each period to the next".
 */
export default function PeriodCalcsEditor({ calcs = [], onChange, periodOptions, periodCats, shownCats }) {
  const patch = (id, change) => onChange(calcs.map((c) => (c.id === id ? { ...c, ...change } : c)));
  const remove = (id) => onChange(calcs.filter((c) => c.id !== id));
  const move = (index, direction) => {
    const next = [...calcs];
    [next[index], next[index + direction]] = [next[index + direction], next[index]];
    onChange(next);
  };

  // A change defaults to the last two shown periods, a CAGR to the first and last.
  const add = (type) => {
    const id = nextColumnId(calcs);
    const keys = shownCats.map((c) => c.key);
    if (type === PERIOD_CALC_TYPES.cagr) {
      return onChange([...calcs, { id, type, from: keys[0], to: keys[keys.length - 1] }]);
    }
    onChange([...calcs, { id, type, from: keys[keys.length - 2], to: keys[keys.length - 1], show: "pct" }]);
  };

  // Year on year (or quarter on quarter, ...): one change per pair of
  // neighbouring shown periods of the same kind.
  const addStepChanges = () => {
    let id = nextColumnId(calcs);
    const added = [];
    shownCats.forEach((c, i) => {
      const next = shownCats.slice(i + 1).find((n) => n.kind === c.kind);
      if (next) added.push({ id: id++, type: PERIOD_CALC_TYPES.change, from: c.key, to: next.key, show: "pct" });
    });
    onChange([...calcs, ...added]);
  };

  const periodSelect = (c, field, placeholder) => (
    <Select
      size="small"
      showSearch
      optionFilterProp="label"
      style={{ width: 150 }}
      placeholder={placeholder}
      value={periodCats.some((p) => p.key === c[field]) ? c[field] : undefined}
      options={periodOptions}
      onChange={(value) => patch(c.id, { [field]: value })}
    />
  );

  return (
    <div>
      {calcs.length === 0 && (
        <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 6 }}>
          None yet. They are shown after the periods, for every business case/scenario and account.
        </div>
      )}

      {calcs.map((c, index) => (
        <div key={c.id} style={rowStyle}>
          <strong style={{ width: 54 }}>{c.type === PERIOD_CALC_TYPES.cagr ? "CAGR" : "Change"}</strong>
          {periodSelect(c, "from", "From")}
          <span>→</span>
          {periodSelect(c, "to", "To")}
          {c.type === PERIOD_CALC_TYPES.change && (
            <Radio.Group
              size="small"
              optionType="button"
              options={CHANGE_SHOW_OPTIONS}
              value={c.show || "pct"}
              onChange={(e) => patch(c.id, { show: e.target.value })}
            />
          )}
          <Input
            size="small"
            allowClear
            style={{ width: 140 }}
            placeholder="Header (optional)"
            value={c.label}
            onChange={(e) => patch(c.id, { label: e.target.value || undefined })}
          />
          <Space size={0} style={{ marginLeft: "auto" }}>
            <Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => move(index, -1)} />
            <Button
              type="text"
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={index === calcs.length - 1}
              onClick={() => move(index, 1)}
            />
            <Tooltip title="Remove">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => remove(c.id)} />
            </Tooltip>
          </Space>
        </div>
      ))}

      <Space size={8} wrap style={{ marginTop: 4 }}>
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => add(PERIOD_CALC_TYPES.change)}>
          Change
        </Button>
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => add(PERIOD_CALC_TYPES.cagr)}>
          CAGR
        </Button>
        <Button size="small" type="dashed" icon={<PlusOutlined />} disabled={shownCats.length < 2} onClick={addStepChanges}>
          Change from each period to the next
        </Button>
      </Space>
    </div>
  );
}
